import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Field,
  Flex,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
} from '@optiaxiom/react';

import FullScreenLoader from '@frontend/components/FullScreenLoader';
import { EMAIL_DOMAIN_SUFFIX } from '@frontend/envConfig';
import { useObjectTitle } from '@frontend/hooks';

import { useGetEmailDomain, useUpdateEmailDomain } from '../hooks';

// Regex for valid subdomain: only lowercase letters, numbers, and hyphens
// Must start and end with alphanumeric characters, hyphens only in middle
const SUBDOMAIN_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

// Date formatting utility function
const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  } catch {
    return 'N/A';
  }
};

function EmailDomainConfiguration() {
  useObjectTitle('Email Domain');
  const [subdomain, setSubdomain] = useState('');
  const [error, setError] = useState('');

  const { emailDomainData, isPending } = useGetEmailDomain();
  const { updateEmailDomain, isUpdating } = useUpdateEmailDomain();

  useEffect(() => {
    if (emailDomainData?.email_domain) {
      // Extract subdomain from full domain
      const domain = emailDomainData.email_domain;
      const subdomain = domain.replace(EMAIL_DOMAIN_SUFFIX, '');
      setSubdomain(subdomain);
    }
  }, [emailDomainData]);

  const validateSubdomain = (value: string) => {
    if (!value) {
      setError('Subdomain is required');
      return false;
    }

    if (value.length < 2) {
      setError('Subdomain must be at least 2 characters long');
      return false;
    }

    if (value.length > 63) {
      setError('Subdomain must be 63 characters or less');
      return false;
    }

    if (!SUBDOMAIN_REGEX.test(value)) {
      setError(
        'Invalid subdomain format. Use only lowercase letters, numbers, and hyphens. Cannot start or end with hyphen.'
      );
      return false;
    }

    setError('');
    return true;
  };

  const handleSubdomainChange = (value: string) => {
    setSubdomain(value.toLowerCase());
    validateSubdomain(value.toLowerCase());
  };

  const handleSave = () => {
    if (validateSubdomain(subdomain)) {
      const fullDomain = `${subdomain}${EMAIL_DOMAIN_SUFFIX}`;
      updateEmailDomain({ email_domain: fullDomain });
    }
  };
  const isVerified = emailDomainData?.email_domain_verified;
  const hasExistingDomain = !!emailDomainData?.email_domain;
  const showInput = !emailDomainData?.email_domain;

  if (isPending) {
    return (
      <Flex>
        <FullScreenLoader />
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" gap="16">
      {hasExistingDomain && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Email Domain</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Last Updated</TableHeaderCell>
              <TableHeaderCell>Updated By</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>
                <Text fontWeight="600">{emailDomainData.email_domain}</Text>
              </TableCell>
              <TableCell>
                <Badge intent={isVerified ? 'success' : 'warning'} variant="subtle">
                  {isVerified ? 'Verified' : 'Pending'}
                </Badge>
              </TableCell>
              <TableCell>
                <Text>{formatDate(emailDomainData.email_domain_updated_at)}</Text>
              </TableCell>
              <TableCell>
                <Text>{emailDomainData.email_domain_updated_by || 'N/A'}</Text>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}

      {showInput && (
        <Flex flexDirection="column" gap="16">
          <Flex flexDirection="row" alignItems="center" gap="8">
            <Flex w="384">
              <Field
                description="Enter a valid subdomain prefix (lowercase letters, numbers, hyphens only)"
                label="Email Domain"
                required
                error={error}
              >
                <Input
                  placeholder="yourcompany"
                  size="md"
                  value={subdomain}
                  onValueChange={handleSubdomainChange}
                  error={!!error}
                  disabled={isUpdating}
                />
              </Field>
            </Flex>
            <Text fontSize="md" color="fg.secondary">
              {EMAIL_DOMAIN_SUFFIX}
            </Text>
          </Flex>

          {subdomain && !error && (
            <Flex flexDirection="row" alignItems="center" gap="16">
              <Text fontSize="sm" color="fg.success">
                Preview: {subdomain}
                {EMAIL_DOMAIN_SUFFIX}
              </Text>
              <Button
                size="sm"
                onClick={handleSave}
                loading={isUpdating}
                disabled={!!error || !subdomain}
              >
                Save Domain
              </Button>
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  );
}

export default EmailDomainConfiguration;
